import React, { useState, useEffect, useCallback } from "react";
import { Select, TextInput, Button } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import {
  FilterParams,
  PropertyTypeOption,
  DateRangePreset,
} from "@/types/price-index";
import { Search } from "@/components/bnp-icons";

interface FilterControlsProps {
  onFilterChange: (filters: FilterParams) => void;
  availableLocations?: string[];
  availablePropertyTypes?: string[];
  isLoading?: boolean;
}

const propertyTypeOptions = [
  { value: "all", label: "All Property Types" },
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "land", label: "Land" },
  { value: "apartment", label: "Apartment" },
  { value: "house", label: "House" },
];

const dateRangePresets: { value: DateRangePreset; label: string }[] = [
  { value: "7d", label: "Last 7 Days" },
  { value: "1m", label: "Last Month" },
  { value: "3m", label: "Last 3 Months" },
  { value: "6m", label: "Last 6 Months" },
  { value: "1y", label: "Last Year" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

const sortByOptions = [
  { value: "date", label: "Date" },
  { value: "price", label: "Price" },
  { value: "location", label: "Location" },
  { value: "propertyType", label: "Property Type" },
];

const sortOrderOptions = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

const FilterControls: React.FC<FilterControlsProps> = ({
  onFilterChange,
  availableLocations = [],
  availablePropertyTypes = [],
  isLoading = false,
}) => {
  const [location, setLocation] = useState<string>("all");
  const [propertyType, setPropertyType] = useState<PropertyTypeOption>("all");
  const [datePreset, setDatePreset] = useState<DateRangePreset>("3m");
  const [customDateRange, setCustomDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [searchQuery, setSearchQuery] = useState("");
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [sortBy, setSortBy] = useState<FilterParams["sortBy"]>("date");
  const [sortOrder, setSortOrder] = useState<FilterParams["sortOrder"]>("desc");

  const [debouncedSearch] = useDebouncedValue(searchQuery, 500);
  const [debouncedMinPrice] = useDebouncedValue(minPrice, 500);
  const [debouncedMaxPrice] = useDebouncedValue(maxPrice, 500);

  const getDateRangeFromPreset = useCallback(
    (preset: DateRangePreset): { start: string; end: string } | undefined => {
      const now = new Date();
      const end = now.toISOString().split("T")[0];

      const startDate = new Date();
      switch (preset) {
        case "7d":
          startDate.setDate(now.getDate() - 7);
          break;
        case "1m":
          startDate.setMonth(now.getMonth() - 1);
          break;
        case "3m":
          startDate.setMonth(now.getMonth() - 3);
          break;
        case "6m":
          startDate.setMonth(now.getMonth() - 6);
          break;
        case "1y":
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        case "all":
          return undefined;
        case "custom":
          if (customDateRange[0] && customDateRange[1]) {
            return {
              start: customDateRange[0].toISOString().split("T")[0],
              end: customDateRange[1].toISOString().split("T")[0],
            };
          }
          return undefined;
        default:
          return undefined;
      }

      return { start: startDate.toISOString().split("T")[0], end };
    },
    [customDateRange],
  );

  useEffect(() => {
    const filters: FilterParams = {
      ...(location !== "all" && { location }),
      ...(propertyType !== "all" && { propertyType }),
      ...(debouncedMinPrice && { minPrice: parseFloat(debouncedMinPrice) }),
      ...(debouncedMaxPrice && { maxPrice: parseFloat(debouncedMaxPrice) }),
      sortBy,
      sortOrder,
    };

    const dateRange = getDateRangeFromPreset(datePreset);
    if (dateRange) {
      filters.dateRange = dateRange;
    }

    onFilterChange(filters);
  }, [
    location,
    propertyType,
    datePreset,
    customDateRange,
    debouncedSearch,
    debouncedMinPrice,
    debouncedMaxPrice,
    sortBy,
    sortOrder,
    onFilterChange,
    getDateRangeFromPreset,
  ]);

  const handleReset = () => {
    setLocation("all");
    setPropertyType("all");
    setDatePreset("3m");
    setCustomDateRange([null, null]);
    setSearchQuery("");
    setMinPrice("");
    setMaxPrice("");
    setSortBy("date");
    setSortOrder("desc");
  };

  const locationOptions = [
    { value: "all", label: "All Locations" },
    ...availableLocations.map((loc) => ({ value: loc, label: loc })),
  ];

  const propertyOptions =
    availablePropertyTypes.length > 0
      ? [
          { value: "all", label: "All Property Types" },
          ...availablePropertyTypes.map((type) => ({
            value: type,
            label: type,
          })),
        ]
      : propertyTypeOptions;

  return (
    <div className="bg-white rounded-xl border border-grey-3 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-main-900">Filter Options</h3>
        <button
          onClick={handleReset}
          className="text-sm text-bnp-cyan hover:text-bnp-dark font-medium transition-colors"
          disabled={isLoading}
        >
          Reset Filters
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Select
          label="Location"
          placeholder="Select location"
          data={locationOptions}
          value={location}
          onChange={(value) => setLocation(value || "all")}
          disabled={isLoading}
          searchable
          clearable
          styles={{
            input: {
              borderColor: "#E1E6F1",
              "&:focus": { borderColor: "#2D96A7" },
            },
          }}
        />

        <Select
          label="Property Type"
          placeholder="Select property type"
          data={propertyOptions}
          value={propertyType}
          onChange={(value) =>
            setPropertyType((value as PropertyTypeOption) || "all")
          }
          disabled={isLoading}
          searchable
          clearable
          styles={{
            input: {
              borderColor: "#E1E6F1",
              "&:focus": { borderColor: "#2D96A7" },
            },
          }}
        />

        <Select
          label="Date Range"
          placeholder="Select date range"
          data={dateRangePresets}
          value={datePreset}
          onChange={(value) =>
            setDatePreset((value as DateRangePreset) || "3m")
          }
          disabled={isLoading}
          styles={{
            input: {
              borderColor: "#E1E6F1",
              "&:focus": { borderColor: "#2D96A7" },
            },
          }}
        />

        <Select
          label="Sort By"
          placeholder="Sort by"
          data={sortByOptions}
          value={sortBy}
          onChange={(value) =>
            setSortBy((value as FilterParams["sortBy"]) || "date")
          }
          disabled={isLoading}
          styles={{
            input: {
              borderColor: "#E1E6F1",
              "&:focus": { borderColor: "#2D96A7" },
            },
          }}
        />
      </div>

      {datePreset === "custom" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <DatePickerInput
            type="range"
            label="Custom Date Range"
            placeholder="Pick dates range"
            value={customDateRange}
            onChange={setCustomDateRange}
            disabled={isLoading}
            clearable
            styles={{
              input: {
                borderColor: "#E1E6F1",
                "&:focus": { borderColor: "#2D96A7" },
              },
            }}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TextInput
          label="Min Price"
          placeholder="Minimum price"
          type="number"
          value={minPrice}
          onChange={(e) => setMinPrice(e.currentTarget.value)}
          disabled={isLoading}
          leftSection={<span className="text-grey-4 text-sm">₦</span>}
          styles={{
            input: {
              borderColor: "#E1E6F1",
              "&:focus": { borderColor: "#2D96A7" },
            },
          }}
        />

        <TextInput
          label="Max Price"
          placeholder="Maximum price"
          type="number"
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.currentTarget.value)}
          disabled={isLoading}
          leftSection={<span className="text-grey-4 text-sm">₦</span>}
          styles={{
            input: {
              borderColor: "#E1E6F1",
              "&:focus": { borderColor: "#2D96A7" },
            },
          }}
        />

        <Select
          label="Sort Order"
          placeholder="Order"
          data={sortOrderOptions}
          value={sortOrder}
          onChange={(value) =>
            setSortOrder((value as FilterParams["sortOrder"]) || "desc")
          }
          disabled={isLoading}
          styles={{
            input: {
              borderColor: "#E1E6F1",
              "&:focus": { borderColor: "#2D96A7" },
            },
          }}
        />
      </div>
    </div>
  );
};

export default FilterControls;
